import React, { useState, useReducer } from "react"
import Select from "react-select"
import { Button, Modal } from "react-bootstrap"
import { injectIntl, FormattedMessage } from "react-intl"

import { SelectOption } from "../../state/helpers/SelectOption"
import priceListMessages from "../helpers/priceListMessages"
import IntlProps from "../../state/helpers/IntlProps"
import { INVENTORY_RULE_KEY, LIMIT_RULE_KEY, NULL_RULE_KEY } from "../../state/price-list/ruleStrategyKeys"
import { getPriceList } from "../../state/price-list/priceListSelectors"
import { connect, ConnectedProps } from "react-redux"
import PricingRow from "../../state/pricing/PricingRow"
import { patchPriceList } from "../../state/price-list/priceListActions"
import PriceList, { LimitRuleDto } from "../../state/price-list/PriceList"
import RowsLimitEditComponent from "../components/RowsLimitEditComponent"
import { getAction } from "../../state/actionlog/actionLogSelector"
import { isActionSuccessTimeout, isActionLoading } from "../../state/actionlog/ActionLog"
import LoadingSpinner from "../../common/spinner/loading.component"
import PriceListValidationRuleInfoComponent from "../components/PriceListValidationRuleInfoComponent"
import ReactTooltip from "react-tooltip"

type OwnProps = {
    onHide: () => void
    priceListId: string
}
const ACTION_ID = "price-list-validation-edit-section"
const PriceListValidationEditModal = (props: Props & IntlProps) => {
    const {priceList} = props
    if (!priceList) {
        return <></>
    }

    const options: SelectOption[] = [
        {
            label: props.intl.formatMessage(priceListMessages[NULL_RULE_KEY]),
            value: NULL_RULE_KEY
        },
        {
            label: props.intl.formatMessage(priceListMessages[INVENTORY_RULE_KEY]),
            value: INVENTORY_RULE_KEY
        },
        {
            label: props.intl.formatMessage(priceListMessages[LIMIT_RULE_KEY]),
            value: LIMIT_RULE_KEY
        },
    ]
    const defaultOption = options
        .find(x => x.value == props.priceList?.getValidationStrategyKey()) || options[0]
    const [selected, setSelected] = useState(defaultOption)
    const [state, dispatch] = useReducer(validationRulesReducer, buildDefaultState(priceList))

    const isLoading = isActionLoading(props.action)
    const updatePriceList = async () => {
        const payload = buildPayload(selected.value, state)
        props.patchPriceList(priceList.getId(), payload, ACTION_ID)
    }

    if (isActionSuccessTimeout(props.action, 50)) {
        props.onHide()
    }

    return (
        <Modal scrollable={true} show={true} onHide={props.onHide}>
            <Modal.Header>
                <Modal.Title>
                    <FormattedMessage
                        id="price_list.edit.validation_rules.modal_title_label"
                        defaultMessage="Rules"
                    />
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{minHeight: "300px"}}>
                {isLoading ? <LoadingSpinner/> : <>
                    <div className="ui-has-modules">
                        <Select
                            value={selected}
                            options={options}
                            onChange={setSelected}
                            isMulti={false}
                            clearable={false}
                        />
                    </div>

                    <span className="ui-has-modules" data-tip data-for={"rule-info-tooltip"}>
                        <ReactTooltip
                            place={"right"}
                            className=""
                            id={"rule-info-tooltip"}
                            type="info"
                            effect="solid"
                            multiline={true}>
                            {
                                // @ts-ignore
                                showRuleInfoTooltip(selected.value)
                            }
                        </ReactTooltip>

                        <small className="text-primary">
                            <FormattedMessage
                                id="price_list.edit.validation_rules.whats_this_label"
                                defaultMessage="What's this?"
                            />
                        </small>
                    </span>

                    {selected.value == LIMIT_RULE_KEY &&
                        <RowsLimitEditComponent
                            updateMaxCapacity={updateMaxCapacity(dispatch)}
                            updateRowLimit={updateRowLimit(dispatch)}
                            rowLimits={state.rowLimits}
                            maxCapacity={state.maxCapacity}
                            rows={priceList.getRows()}
                        />
                    }
                </>}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" disabled={isLoading} onClick={props.onHide}>
                    <FormattedMessage
                        id="core.modal.close_button_label"
                        defaultMessage="Close"
                    />
                </Button>
                <Button variant="primary" disabled={isLoading} onClick={updatePriceList}>
                    <FormattedMessage
                        id="core.modal.save_changes_button_label"
                        defaultMessage="Save Changes"
                    />
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

const showRuleInfoTooltip = (ruleKey: string) => {
    return (
        <PriceListValidationRuleInfoComponent ruleKey={ruleKey}/>
    )
}

const buildPayload = (selectedValue, state) => {
    const payload: { key: string, config?: object } = {key: ""}
    switch (selectedValue) {
    case NULL_RULE_KEY:
        payload.key = NULL_RULE_KEY
        break
    case INVENTORY_RULE_KEY:
        payload.key = INVENTORY_RULE_KEY
        break
    case LIMIT_RULE_KEY:
        payload.key = LIMIT_RULE_KEY
        payload.config = {
            rowLimits: state.rowLimits,
            maxCapacity: state.maxCapacity,
        }
        break
    }
    return {fulfillmentDto: payload}
}

const buildDefaultState = (priceList: PriceList) => {
    const limit: LimitRuleDto = priceList.getValidationConfig() as LimitRuleDto

    const rowLimits: object = limit.rowLimits || {}
    let maxCapacity: number | undefined = limit.maxCapacity
    const isMaxCapacityDefined: boolean = limit.isMaxCapacitySet
    if (!isMaxCapacityDefined) {
        maxCapacity = Object.values(rowLimits).reduce((a, b) => a + b, 0)
    }
    return {
        rowLimits: rowLimits,
        maxCapacity: maxCapacity,
        isMaxCapacityDefined: isMaxCapacityDefined
    }
}

const UPDATE_MAX_CAPACITY = "update-max-capacity"
const UPDATE_ROW_LIMIT = "update-row-limit"
const validationRulesReducer = (state, action) => {
    switch (action.type) {
    case UPDATE_ROW_LIMIT:
        const rowInfo = action.payload
        state.rowLimits[rowInfo.rowId] = rowInfo.rowLimit
        if (!state.isMaxCapacityDefined) {
            const rowLimits = Object.values(state.rowLimits) as number[]
            state.maxCapacity = rowLimits.reduce((a, b) => a + b, 0)
        }
        return {...state}
    case UPDATE_MAX_CAPACITY:
        state.maxCapacity = action.payload.maxCapacity
        state.isMaxCapacityDefined = true
        return {...state}
    default:
        return state
    }
}

const updateMaxCapacity = (dispatch) => (newCapacity: number) => {
    dispatch({
        type: UPDATE_MAX_CAPACITY,
        payload: {
            maxCapacity: newCapacity,
        }
    })
}

const updateRowLimit = (dispatch) => (row: PricingRow, newLimit: number) => {
    dispatch({
        type: UPDATE_ROW_LIMIT,
        payload: {
            rowId: row.getId(),
            rowLimit: newLimit,
        }
    })
}

const mapState = (state, props: OwnProps) => {
    return {
        ...props,
        priceList: getPriceList(state, props.priceListId),
        action: getAction(state, ACTION_ID),
    }
}
const mapDispatch = {
    patchPriceList,
}
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(PriceListValidationEditModal))


// WEBPACK FOOTER //
// ./src/price-list/modal/PriceListValidationEditModal.tsx